import { z } from "zod";

import { AppointmentStatus, AppointmentType } from "@/shared/models/appointment";
import { RelationshipToSubscriber } from "@/shared/models/coverage";
import { SchedulingPriority } from "@/shared/models/referral";

const patientOrderTypeSchema = z.enum(["consult", "lab", "imaging", "prescription", "procedure", "other"]);

const isoDateSchema = z.string().refine(
  (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;

    const parsed = new Date(`${value}T00:00:00.000Z`);

    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  },
  { message: "Expected a valid date in YYYY-MM-DD format" }
);

const northAmericanPhoneSchema = z
  .string()
  .transform((value) => value.replace(/\D/g, ""))
  .transform((value) => (value.length === 11 && value.startsWith("1") ? value.slice(1) : value))
  .refine((value) => /^\d{10}$/.test(value), "Expected a 10-digit North American phone number")
  .transform((value) => `+1${value}`);

export const integrationFixtureKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(160)
  .regex(/^[a-z0-9][a-z0-9._/-]*$/, "Use lowercase letters, numbers, '.', '_', '-', or '/'");

export const integrationFixturePatientSchema = z.object({
  givenName: z.string().trim().min(1),
  familyName: z.string().trim().min(1),
  dateOfBirth: isoDateSchema,
  phoneNumber: northAmericanPhoneSchema,
});

export const integrationFixtureCoverageSchema = z.object({
  key: integrationFixtureKeySchema,
  payer: z.string().trim().min(1),
  plan: z.string().trim().min(1),
  memberId: z.string().trim().min(1),
  customerPayer: z.string().trim().min(1).nullable().default(null),
  lineOfBusiness: z.string().trim().min(1).default("Commercial"),
  relationshipToSubscriber: z.nativeEnum(RelationshipToSubscriber).default(RelationshipToSubscriber.SELF),
  order: z.number().int().positive(),
});

export const integrationFixtureAppointmentSchema = z
  .object({
    key: integrationFixtureKeySchema,
    practitionerId: z.string().uuid().optional(),
    practitionerEhrId: z.string().trim().min(1).optional(),
    locationId: z.string().uuid().optional(),
    locationEhrId: z.string().trim().min(1).optional(),
    start: z.string().datetime({ offset: true }),
    end: z.string().datetime({ offset: true }),
    type: z.nativeEnum(AppointmentType).default(AppointmentType.FOLLOW_UP),
    status: z.nativeEnum(AppointmentStatus).default(AppointmentStatus.SCHEDULED),
    ehrAppointmentTypeId: z.string().trim().min(1).nullable().default(null),
    reason: z.string().trim().min(1).nullable().default(null),
  })
  .superRefine((appointment, ctx) => {
    if ((appointment.practitionerId === undefined) === (appointment.practitionerEhrId === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["practitionerId"],
        message: "Specify exactly one of practitionerId or practitionerEhrId",
      });
    }

    if ((appointment.locationId === undefined) === (appointment.locationEhrId === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["locationId"],
        message: "Specify exactly one of locationId or locationEhrId",
      });
    }

    const durationMs = new Date(appointment.end).getTime() - new Date(appointment.start).getTime();

    if (durationMs <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["end"], message: "Appointment end must follow start" });
    } else if (durationMs < 60_000) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["end"],
        message: "Appointment duration must be at least one minute",
      });
    }
  });

export const integrationFixturePatientOrderSchema = z.object({
  key: integrationFixtureKeySchema,
  orderType: patientOrderTypeSchema.default("other"),
  status: z.string().trim().min(1),
  dateOrdered: z.string().datetime({ offset: true }).nullable().default(null),
  description: z.string().trim().min(1),
  documentDescription: z.string().trim().min(1).nullable().default(null),
  departmentEhrId: z.string().trim().min(1).nullable().default(null),
  deletedAt: z.string().datetime({ offset: true }).nullable().default(null),
});

export const integrationFixtureReferralSchema = z
  .object({
    key: integrationFixtureKeySchema,
    status: z.string().trim().min(1).default("not_started"),
    reasonForReferral: z.string().trim().min(1),
    schedulingPriority: z.nativeEnum(SchedulingPriority).default(SchedulingPriority.ROUTINE),
    dateOfReferral: isoDateSchema.nullable().default(null),
    receivingLocationId: z.string().uuid().optional(),
    receivingLocationEhrId: z.string().trim().min(1).optional(),
  })
  .superRefine((referral, ctx) => {
    if (referral.receivingLocationId && referral.receivingLocationEhrId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["receivingLocationId"],
        message: "Specify at most one of receivingLocationId or receivingLocationEhrId",
      });
    }
  });

const integrationFixtureDefinitionFields = {
  fixtureKey: integrationFixtureKeySchema,
  groupId: z.string().uuid(),
  patient: integrationFixturePatientSchema,
  coverages: z.array(integrationFixtureCoverageSchema).max(4).default([]),
  appointments: z.array(integrationFixtureAppointmentSchema).max(20).default([]),
  patientOrders: z.array(integrationFixturePatientOrderSchema).max(20).default([]),
  referrals: z.array(integrationFixtureReferralSchema).max(10).default([]),
};

function validateUniqueResourceKeys(
  fixture: {
    coverages: Array<{ key: string }>;
    appointments: Array<{ key: string }>;
    patientOrders: Array<{ key: string }>;
    referrals: Array<{ key: string }>;
  },
  ctx: z.RefinementCtx
): void {
  for (const [path, keys] of [
    ["coverages", fixture.coverages.map(({ key }) => key)],
    ["appointments", fixture.appointments.map(({ key }) => key)],
    ["patientOrders", fixture.patientOrders.map(({ key }) => key)],
    ["referrals", fixture.referrals.map(({ key }) => key)],
  ] as const) {
    if (new Set(keys).size !== keys.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message: `${path} keys must be unique` });
    }
  }
}

export const integrationFixtureDefinitionSchema = z
  .object(integrationFixtureDefinitionFields)
  .superRefine(validateUniqueResourceKeys);

export type IntegrationFixtureDefinitionInput = z.input<typeof integrationFixtureDefinitionSchema>;

export const provisionIntegrationFixtureRequestSchema = z
  .object({ ...integrationFixtureDefinitionFields, leaseId: z.string().uuid() })
  .superRefine(validateUniqueResourceKeys);

export type ProvisionIntegrationFixtureRequest = z.infer<typeof provisionIntegrationFixtureRequestSchema>;

export const releaseIntegrationFixtureLeaseRequestSchema = z.object({
  fixtureKey: integrationFixtureKeySchema,
  groupId: z.string().uuid(),
  leaseId: z.string().uuid(),
});

export type ReleaseIntegrationFixtureLeaseRequest = z.infer<typeof releaseIntegrationFixtureLeaseRequestSchema>;

export const getIntegrationFixtureLeaseRequestSchema = z.object({ groupId: z.string().uuid() });

export type GetIntegrationFixtureLeaseRequest = z.infer<typeof getIntegrationFixtureLeaseRequestSchema>;

export const integrationFixtureLeaseSchema = z.object({
  groupId: z.string().uuid(),
  fixtureKey: integrationFixtureKeySchema,
  leaseId: z.string().uuid(),
  expiresAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});

export type IntegrationFixtureLease = z.infer<typeof integrationFixtureLeaseSchema>;

export const getIntegrationFixtureLeaseResponseSchema = z.object({
  lease: integrationFixtureLeaseSchema.nullable(),
});

export type GetIntegrationFixtureLeaseResponse = z.infer<typeof getIntegrationFixtureLeaseResponseSchema>;

export const recoverIntegrationFixtureLeaseRequestSchema = z.object({
  groupId: z.string().uuid(),
  expectedFixtureKey: integrationFixtureKeySchema,
  expectedLeaseId: z.string().uuid(),
  force: z.literal(true),
});

export type RecoverIntegrationFixtureLeaseRequest = z.infer<typeof recoverIntegrationFixtureLeaseRequestSchema>;

export const provisionIntegrationFixtureResponseSchema = z.object({
  fixtureId: z.string().uuid(),
  fixtureKey: integrationFixtureKeySchema,
  generation: z.number().int().positive(),
  patientId: z.string().uuid(),
  coverageIds: z.record(z.string().uuid()),
  appointmentIds: z.record(z.string().uuid()),
  patientOrderIds: z.record(z.string().uuid()),
  referralIds: z.record(z.string().uuid()),
});

export type ProvisionIntegrationFixtureResponse = z.infer<typeof provisionIntegrationFixtureResponseSchema>;

export const releaseIntegrationFixtureLeaseResponseSchema = z.object({ released: z.literal(true) });

export type ReleaseIntegrationFixtureLeaseResponse = z.infer<typeof releaseIntegrationFixtureLeaseResponseSchema>;

export const recoverIntegrationFixtureLeaseResponseSchema = z.object({ recovered: z.boolean() });

export type RecoverIntegrationFixtureLeaseResponse = z.infer<typeof recoverIntegrationFixtureLeaseResponseSchema>;
